import { 
  ChatRequest, 
  ChatResponse, 
  HealthResponse, 
  StartTicketDraftRequest, 
  SubmitAnswersRequest, 
  TicketCommentResponse, 
  TicketDraftActionRequest, 
  TicketDraftResponse, 
  TicketStatusResponse,
  AzureTicketRequest,
} from "types/dse-knowledge";

const API_BASE = process.env.REACT_APP_DSE_API_URL || "https://knowledge-api.analytics-apps-dev.ph-dse.int.bayer.com";
const API_KEY = process.env.REACT_APP_DSE_API_KEY || "";

async function apiCall<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(API_KEY && { "x-api-key": API_KEY }),
    "Authorization": "pOIRfWdHfboI1B70FAKxk52im9glZpSC",
    "x-api-key": "sSYkzN7PKjpuu6fdfHnP6dzd9UmDobB9tePNFZGi",
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: { ...headers, ...(options.headers as Record<string, string>) },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `API error: ${response.status}`);
  }

  // FIXED: Cast to T to solve @typescript-eslint/no-unsafe-return
  return (await response.json()) as T;
}

// ============ CHAT ============

export async function sendChat(request: ChatRequest): Promise<ChatResponse> {
  return apiCall<ChatResponse>("/chat", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

/**
 * Creates a ticket in Azure DevOps via the /ticket endpoint.
 */
export const submitAzureTicket = async (data: AzureTicketRequest): Promise<TicketDraftResponse> => {
  return apiCall<TicketDraftResponse>("/ticket", {
    method: "POST",
    headers: {
      "Accept": "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
};

// ============ TICKET FLOW ============

export async function startTicketDraft(
  request: StartTicketDraftRequest,
): Promise<TicketDraftResponse> {
  return apiCall<TicketDraftResponse>("/chat/ticket/start", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function submitTicketAnswers(
  request: SubmitAnswersRequest,
): Promise<TicketDraftResponse> {
  return apiCall<TicketDraftResponse>("/chat/ticket/answers", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function ticketDraftAction(
  request: TicketDraftActionRequest,
): Promise<TicketDraftResponse> {
  return apiCall<TicketDraftResponse>("/chat/ticket/action", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

/**
 * Creates a ticket for the current chat session.
 * @param data - contains the unique session_id
 */
export const createTicket = async (data: { session_id: string }): Promise<TicketDraftResponse> => {
  const url = `/chat/ticket/create?session_id=${encodeURIComponent(data.session_id)}`;

  // FIXED: Removed 'await' before apiCall to solve @typescript-eslint/return-await
  return apiCall<TicketDraftResponse>(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}), 
  });
};

// ============ TICKET STATUS ============

export async function getTicketStatus(ticketId: number): Promise<TicketStatusResponse> {
  return apiCall<TicketStatusResponse>(`/chat/ticket/${ticketId}/status`);
}

export async function addTicketComment(
  ticketId: number,
  comment: string,
): Promise<TicketCommentResponse> {
  return apiCall<TicketCommentResponse>(`/chat/ticket/${ticketId}/comment`, {
    method: "POST",
    body: JSON.stringify({ ticket_id: ticketId, comment }),
  });
}

// ============ HEALTH ============

export async function checkHealth(): Promise<HealthResponse> {
  return apiCall<HealthResponse>("/health");
}